/**
 * Prediction tab: component mode (health ring, predicted failure with its
 * severity, confidence, trend, recommended action, spare parts) or live mode
 * for RFIMS readings (margin ring, forecast text, confidence, trend,
 * recommendation; no spare parts).
 */
import { ArrowDownRight, ArrowUpRight, Minus, Package } from 'lucide-react';
import { Chip } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import type { ComponentPrediction, LivePrediction, Severity } from '../../adapters/analysis';
import { HealthRing, ConfidenceBar } from '../../components/bars';
import { Card, EmptyState, Row } from '../../components/layout';
import { fmtDate } from '../../lib/format';
import { stockBadge } from '../../lib/inventoryBadge';

/** The fields of a spare part the cards show (an inventory part or the manager's status). */
export interface SparePart {
  partNumber: string;
  name: string;
  quantity: number;
  minStock: number;
  leadTimeDays: number;
  location: string;
}

function severityClass(s: Severity): string {
  return s === 'critical' ? 'text-warning' : s === 'warning' ? 'text-caution' : 'text-ink';
}

function TrendIcon({ direction }: { direction: string }) {
  const d = direction.toLowerCase();
  if (d.startsWith('ris')) return <ArrowUpRight className="h-3.5 w-3.5 text-caution" aria-label="Rising" />;
  if (d.startsWith('dec') || d.startsWith('fal')) return <ArrowDownRight className="h-3.5 w-3.5 text-caution" aria-label="Falling" />;
  return <Minus className="h-3.5 w-3.5 text-ink-3" aria-label="Stable" />;
}

export function PredictionCard({ prediction, inventory }: { prediction: ComponentPrediction | LivePrediction | null; inventory: SparePart | null }) {
  if (!prediction) return <EmptyState title="Insufficient historical data for a prediction." />;

  if (prediction.mode === 'live') {
    const p = prediction;
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-4">
          <HealthRing value={p.margin} label="Margin" size={96} />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider text-ink-3">Forecast</div>
            <div className={cn('text-sm font-medium leading-snug', severityClass(p.severity))}>{p.forecastText}</div>
            <div className="mt-1 text-[11px] text-ink-3">
              Limits {p.limits.bidir ? `${p.limits.lo} to ${p.limits.hi}` : `up to ${p.limits.hi}`}
              {p.limits.unit ? ` ${p.limits.unit}` : ''}
            </div>
          </div>
        </div>
        <Card>
          <Row label="Confidence">
            <ConfidenceBar value={p.confidence} className="justify-end" />
          </Row>
          <Row label="Trend">
            <span className="inline-flex items-center gap-1">
              <TrendIcon direction={p.trendText} /> {p.trendText}
            </span>
          </Row>
          <Row label="Model">
            <Chip tone="live">Live threshold forecast</Chip>
          </Row>
        </Card>
        <Card title="Recommended action">
          <p className="leading-snug text-ink">{p.recommendedAction}</p>
        </Card>
      </div>
    );
  }

  const p = prediction;
  const days = Math.round(p.daysToFailure);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-4">
        <HealthRing value={p.health} label="Health" size={96} />
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-ink-3">Predicted failure</div>
          <div className={cn('font-mono text-sm font-medium', severityClass(p.severity))}>{fmtDate(p.failureDateMs)}</div>
          <div className={cn('text-[11px]', severityClass(p.severity))}>
            in {days} days
            {p.severity === 'critical' ? ', critical' : p.severity === 'warning' ? ', plan now' : ''}
          </div>
        </div>
      </div>
      <Card>
        <Row label="Confidence">
          <ConfidenceBar value={p.confidence} className="justify-end" />
        </Row>
        <Row label="Trend">
          <span className="inline-flex items-center gap-1">
            <TrendIcon direction={p.trend.direction} /> {p.trend.text}
          </span>
        </Row>
        <Row label="Profile">
          <span className="text-ink-2">
            {p.profile.type}, {p.profile.failMode}
          </span>
        </Row>
      </Card>
      <Card title="Recommended action">
        <p className="leading-snug text-ink">{p.recommendedAction}</p>
      </Card>
      <Card title="Spare parts">
        {inventory ? (
          <div className="flex items-start gap-2">
            <Package className="mt-0.5 h-4 w-4 shrink-0 text-ink-3" />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-ink">{inventory.name}</span>
                <Chip tone={stockBadge(inventory).tone}>{stockBadge(inventory).label}</Chip>
              </div>
              <div className="text-[11px] text-ink-3">
                {inventory.partNumber}, qty {inventory.quantity}, lead time {inventory.leadTimeDays} days, {inventory.location}
              </div>
              {inventory.quantity === 0 && <div className="mt-1 text-[11px] text-warning">Out of stock, order immediately.</div>}
            </div>
          </div>
        ) : (
          <span className="text-ink-3">No spare parts data for this subsystem.</span>
        )}
      </Card>
    </div>
  );
}
